import { Service, computed, effect, inject, signal } from '@angular/core';
import {
  SaphirIdpService,
  SaphirSessionStore,
  SaphirUser as IdentityUser,
} from '@saphir/auth-client';
import { SaphirAuthContext, SaphirUser } from '@saphir/module-contract';

import { environment } from '../../environments/environment';
import { CompanyContextService } from './company-context.service';

/**
 * The implementation behind `SAPHIR_AUTH` — the auth context every module
 * injects.
 *
 * Two modes, chosen at build time by `environment.auth.useMockAuth`:
 *
 * - Real: sign-in runs through Saphir Identity via `@saphir/auth-client`
 *   (redirect flow, callback, silent renewal — all the library's business).
 *   This service only adapts the library's session to the module contract.
 * - Mock: a fake session is granted at startup so the shell and its modules
 *   can be developed without a registered client id or a reachable Identity.
 *
 * Permissions come from UMC, never from the token — deliberately. The token
 * carries identity only (`sub`, `sid`, display fields); rights live in UMC
 * and change while a token is in someone's hands, so a token asserting them
 * would keep asserting them until it expired. `CompanyContextService` fetches
 * them per company; the ACTIVE company's set is what modules see.
 */
@Service()
export class AuthService implements SaphirAuthContext {
  /** Library services exist only when the build signs in through Identity. */
  private readonly store = environment.auth.useMockAuth ? null : inject(SaphirSessionStore);
  private readonly idp = environment.auth.useMockAuth ? null : inject(SaphirIdpService);

  private readonly companies = inject(CompanyContextService);

  private readonly mockUser = signal<SaphirUser | null>(null);

  /** The shape modules see — no token, no transport details. */
  readonly user = computed<StaffAwareUser | null>(() => {
    const identity = this.store ? toContractUser(this.store.user()) : this.mockUser();

    if (!identity) {
      return null;
    }

    return {
      ...identity,
      // UMC often knows a better display name than the token carries.
      displayName: this.companies.displayName() ?? identity.displayName,
      permissions: this.companies.activePermissions(),
      // Service staff above the tenancy model (contract 0.5.2) - a module hides
      // its development surfaces from everyone else.
      isSuperAdmin: this.companies.isSuperAdmin(),
    };
  });

  readonly isAuthenticated = computed(() =>
    this.store ? this.store.isAuthenticated() : this.mockUser() !== null,
  );

  /**
   * The signed-in person's email, shown under the name in the account menu.
   * Deliberately not part of the module contract — modules identify people
   * by `id`. Null when the token carries none: social sign-ins often omit it.
   */
  readonly email = computed<string | null>(() => {
    if (this.store) {
      return this.store.user()?.email ?? null;
    }

    return this.mockUser() ? 'developer@saphir.local' : null;
  });

  /**
   * Bearer token for the mock-mode interceptor. In real mode this stays null
   * on purpose: `saphirTokenInterceptor` owns the real token — attaching it
   * here as well would bypass the renewal logic that keeps it valid.
   */
  readonly accessToken = computed(() => (this.store ? null : this.mockAccessToken()));

  private readonly mockAccessToken = computed(() =>
    this.mockUser() !== null ? 'mock-access-token' : null,
  );

  constructor() {
    this.applyMockSession();

    // Who the user works AS comes from UMC; fetch it once there is a session
    // to fetch it for. `load()` is idempotent, so re-runs cost nothing.
    effect(() => {
      if (this.isAuthenticated()) {
        this.companies.load();
      }
    });
  }

  hasPermission(permission: string): boolean {
    return this.user()?.permissions.includes(permission) ?? false;
  }

  /**
   * Ends the session. Real mode redirects through Identity's `/connect/logout`
   * — the session is shared across products, so ending it is Identity's job,
   * not a local storage wipe.
   */
  logout(): void {
    if (this.idp) {
      this.idp.logout();

      return;
    }

    this.mockUser.set(null);
  }

  /**
   * Signs a fake user in at startup when the environment asks for mock auth.
   *
   * Without a session every module would develop against a signed-out shell —
   * permissions unchecked, no bearer token on any request. `useMockAuth` is
   * false in every environment but development, and the warning below makes it
   * impossible to mistake for the real thing.
   */
  private applyMockSession(): void {
    if (environment.auth.useMockAuth) {
      console.warn(
        '[auth] Mock authentication is on: everyone is signed in as a developer account.' +
          ' This must never be the case outside development.',
      );

      // Permissions deliberately absent: they come from the (mock) company
      // context, the same path the real mode takes.
      this.mockUser.set({
        id: 'mock-user',
        displayName: 'developer',
        permissions: [],
      });
    }
  }
}

/**
 * Maps the identity the token describes onto the module contract's user.
 *
 * `subject` is the stable id, the same in every Saphir product — exactly what
 * modules should link their records against. The display name degrades
 * gracefully: real name, then email, then the bare subject — the token may
 * omit any of the display fields.
 */
/**
 * `SaphirUser.isSuperAdmin` of contract 0.5.2, typed here until the lockfile is on that version (the feed still
 * serves 0.5.1 while the pipeline is down). Drop this alias with the bump.
 */
type StaffAwareUser = SaphirUser & { readonly isSuperAdmin: boolean };

export function toContractUser(user: IdentityUser | null): SaphirUser | null {
  return user
    ? {
        id: user.subject,
        displayName: user.name ?? user.email ?? user.subject,
        permissions: [],
      }
    : null;
}
